import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  DollarSign, 
  Home, 
  Wrench,
  Bot,
  Sparkles,
  CheckCircle,
  Clock,
  AlertCircle,
  Landmark,
  TrendingDown,
  Zap,
  PiggyBank,
  BookOpen,
  FileText,
  Receipt,
  Upload,
  Mail,
  Phone,
  Pencil
} from "lucide-react";

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "-";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(value);
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return "-";
  return `${value.toFixed(2)}%`;
};

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const hasFetched = useRef(false);
  const currentId = useRef(id);
  
  const [property, setProperty] = useState(null);
  const [units, setUnits] = useState([]);
  const [rentCollections, setRentCollections] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [mortgagePayments, setMortgagePayments] = useState([]);
  const [ledgerTransactions, setLedgerTransactions] = useState([]);
  const [propertyExpenses, setPropertyExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Edit states
  const [editingUnit, setEditingUnit] = useState(null);
  
  // Dialog states - using simple open/close without complex logic
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  const [rentDialogOpen, setRentDialogOpen] = useState(false);
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [mortgagePaymentDialogOpen, setMortgagePaymentDialogOpen] = useState(false);
  const [ledgerDialogOpen, setLedgerDialogOpen] = useState(false);
  
  // AI states - separate to prevent coupling
  const [flipDialogOpen, setFlipDialogOpen] = useState(false);
  const [flipLoading, setFlipLoading] = useState(false);
  const [flipResult, setFlipResult] = useState(null);
  
  const [mortgageAnalysisOpen, setMortgageAnalysisOpen] = useState(false);
  const [mortgageAnalysisLoading, setMortgageAnalysisLoading] = useState(false);
  const [mortgageAnalysisResult, setMortgageAnalysisResult] = useState(null);
  
  // Receipt scanning states
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptScanning, setReceiptScanning] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState("");
  const [receiptResult, setReceiptResult] = useState(null);
  
  // Form states
  const [unitForm, setUnitForm] = useState({
    unit_number: "", rent_amount: "", deposit: "", tenant_name: "", tenant_email: "", tenant_phone: "", is_occupied: false, lease_start: "", lease_end: ""
  });
  
  const [rentForm, setRentForm] = useState({
    unit_id: "", amount: "", payment_method: "", notes: "", date: new Date().toISOString().split('T')[0]
  });
  
  const [maintenanceForm, setMaintenanceForm] = useState({
    unit_id: "", title: "", description: "", estimated_cost: ""
  });

  const [mortgagePaymentForm, setMortgagePaymentForm] = useState({
    amount: "", extra_principal: "", payment_date: new Date().toISOString().split('T')[0], notes: ""
  });

  const [ledgerForm, setLedgerForm] = useState({
    transaction_type: "expense",
    category: "",
    amount: "",
    description: "",
    date: new Date().toISOString().split('T')[0],
    unit_id: "",
    vendor: "",
    is_tax_deductible: false
  });

  useEffect(() => {
    if (currentId.current !== id) {
      hasFetched.current = false;
      currentId.current = id;
      setLoading(true);
      setFlipResult(null);
      setMortgageAnalysisResult(null);
    }
    
    if (hasFetched.current) return;
    
    const fetchData = async () => {
      try {
        const [propRes, unitsRes, rentRes, maintRes, ledgerRes, expensesRes] = await Promise.all([
          api.get(`/properties/${id}`),
          api.get(`/units?property_id=${id}`),
          api.get(`/rent-collections?property_id=${id}`),
          api.get(`/maintenance?property_id=${id}`),
          api.get(`/ledger/transactions?property_id=${id}`).catch(() => ({ data: [] })),
          api.get(`/expenses`).catch(() => ({ data: [] }))
        ]);
        setProperty(propRes.data);
        setUnits(unitsRes.data);
        setRentCollections(rentRes.data);
        setMaintenance(maintRes.data);
        setLedgerTransactions(ledgerRes.data || []);
        // Filter expenses to only this property
        setPropertyExpenses((expensesRes.data || []).filter(e => e.property_id === id));
        
        if (propRes.data.has_mortgage) {
          try {
            const mortgageRes = await api.get(`/properties/${id}/mortgage-payments`);
            setMortgagePayments(mortgageRes.data.payments || []);
          } catch (e) { /* No payments yet */ }
        }
        
        hasFetched.current = true;
      } catch (error) {
        toast.error("Failed to fetch property data");
        navigate("/real-estate");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, api, navigate]);

  const refreshData = async () => {
    try {
      const [propRes, unitsRes, rentRes, maintRes, ledgerRes, expensesRes] = await Promise.all([
        api.get(`/properties/${id}`),
        api.get(`/units?property_id=${id}`),
        api.get(`/rent-collections?property_id=${id}`),
        api.get(`/maintenance?property_id=${id}`),
        api.get(`/ledger/transactions?property_id=${id}`).catch(() => ({ data: [] })),
        api.get(`/expenses`).catch(() => ({ data: [] }))
      ]);
      setProperty(propRes.data);
      setUnits(unitsRes.data);
      setRentCollections(rentRes.data);
      setMaintenance(maintRes.data);
      setLedgerTransactions(ledgerRes.data || []);
      setPropertyExpenses((expensesRes.data || []).filter(e => e.property_id === id));
      
      if (propRes.data.has_mortgage) {
        try {
          const mortgageRes = await api.get(`/properties/${id}/mortgage-payments`);
          setMortgagePayments(mortgageRes.data.payments || []);
        } catch (e) { /* ignore */ }
      }
    } catch (error) {
      toast.error("Failed to refresh data");
    }
  };

  const handleDeleteProperty = async () => {
    if (!window.confirm("Are you sure? This will delete all units and related data.")) return;
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Property deleted");
      navigate("/real-estate");
    } catch (error) {
      toast.error("Failed to delete property");
    }
  };

  const handleAddUnit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    
    try {
      const payload = {
        property_id: id, 
        ...unitForm,
        rent_amount: parseFloat(unitForm.rent_amount),
        deposit: parseFloat(unitForm.deposit || 0)
      };

      if (editingUnit) {
        await api.put(`/units/${editingUnit.id}`, payload);
        toast.success("Unit updated");
      } else {
        await api.post("/units", payload);
        toast.success("Unit added");
      }
      
      setUnitDialogOpen(false);
      setEditingUnit(null);
      setUnitForm({ unit_number: "", rent_amount: "", deposit: "", tenant_name: "", tenant_email: "", tenant_phone: "", is_occupied: false, lease_start: "", lease_end: "" });
      await refreshData();
    } catch (error) {
      console.error("Unit error:", error);
      toast.error(error.response?.data?.detail || "Failed to save unit");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditUnit = (unit) => {
    setEditingUnit(unit);
    setUnitForm({
      unit_number: unit.unit_number || "",
      rent_amount: unit.rent_amount?.toString() || "",
      deposit: unit.deposit?.toString() || "",
      tenant_name: unit.tenant_name || "",
      tenant_email: unit.tenant_email || "",
      tenant_phone: unit.tenant_phone || "",
      is_occupied: unit.is_occupied || false,
      lease_start: unit.lease_start || "",
      lease_end: unit.lease_end || ""
    });
    setUnitDialogOpen(true);
  };

  const handleUnitDialogClose = (open) => {
    setUnitDialogOpen(open);
    if (!open) {
      setEditingUnit(null);
      setUnitForm({ unit_number: "", rent_amount: "", deposit: "", tenant_name: "", tenant_email: "", tenant_phone: "", is_occupied: false, lease_start: "", lease_end: "" });
    }
  };

  const handleCollectRent = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    
    try {
      await api.post("/rent-collections", {
        property_id: id, ...rentForm,
        amount: parseFloat(rentForm.amount)
      });
      toast.success("Rent collected");
      setRentDialogOpen(false);
      setRentForm({ unit_id: "", amount: "", payment_method: "", notes: "", date: new Date().toISOString().split('T')[0] });
      await refreshData();
    } catch (error) {
      console.error("Rent collect error:", error);
      toast.error(error.response?.data?.detail || "Failed to record rent");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMaintenance = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    
    try {
      await api.post("/maintenance", {
        property_id: id, ...maintenanceForm,
        estimated_cost: parseFloat(maintenanceForm.estimated_cost || 0)
      });
      toast.success("Maintenance request added");
      setMaintenanceDialogOpen(false);
      setMaintenanceForm({ unit_id: "", title: "", description: "", estimated_cost: "" });
      await refreshData();
    } catch (error) {
      console.error("Maintenance add error:", error);
      toast.error(error.response?.data?.detail || "Failed to add maintenance");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteMaintenance = async (maintId, estimatedCost) => {
    const cost = window.prompt("Enter actual cost:", estimatedCost);
    if (cost === null) return;
    try {
      await api.put(`/maintenance/${maintId}/complete?actual_cost=${parseFloat(cost)}`);
      toast.success("Maintenance completed");
      refreshData();
    } catch (error) {
      toast.error("Failed to complete maintenance");
    }
  };

  const handleMortgagePayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/properties/${id}/mortgage-payment`, {
        amount: parseFloat(mortgagePaymentForm.amount),
        extra_principal: parseFloat(mortgagePaymentForm.extra_principal || 0),
        payment_date: mortgagePaymentForm.payment_date,
        notes: mortgagePaymentForm.notes
      });
      toast.success("Payment recorded");
      setMortgagePaymentDialogOpen(false);
      setMortgagePaymentForm({ amount: "", extra_principal: "", payment_date: new Date().toISOString().split('T')[0], notes: "" });
      refreshData();
    } catch (error) {
      toast.error("Failed to record payment");
    }
  };

  // AI Functions - completely separate from dialog state
  const runFlipAnalysis = async () => {
    if (flipLoading) return;
    setFlipLoading(true);
    setFlipResult(null);
    try {
      const res = await api.post("/ai/house-flipping", null, {
        params: { address: property.address, purchase_price: property.purchase_price }
      });
      setFlipResult(res.data.analysis);
    } catch (error) {
      toast.error("AI analysis failed");
    } finally {
      setFlipLoading(false);
    }
  };

  const runMortgageAnalysis = async () => {
    if (mortgageAnalysisLoading) return;
    setMortgageAnalysisLoading(true);
    setMortgageAnalysisResult(null);
    try {
      const res = await api.post(`/properties/${id}/mortgage-analysis`);
      setMortgageAnalysisResult(res.data);
    } catch (error) {
      toast.error("AI analysis failed");
    } finally {
      setMortgageAnalysisLoading(false);
    }
  };

  const handleAddLedgerTransaction = async (e) => {
    e.preventDefault();
    try {
      await api.post("/ledger/transactions", {
        property_id: id,
        unit_id: ledgerForm.unit_id || null,
        transaction_type: ledgerForm.transaction_type,
        category: ledgerForm.category,
        amount: parseFloat(ledgerForm.amount),
        description: ledgerForm.description,
        date: ledgerForm.date,
        vendor: ledgerForm.vendor || null,
        is_tax_deductible: ledgerForm.is_tax_deductible
      });
      toast.success(`${ledgerForm.transaction_type === 'income' ? 'Income' : 'Expense'} recorded`);
      setLedgerDialogOpen(false);
      setLedgerForm({ transaction_type: "expense", category: "", amount: "", description: "", date: new Date().toISOString().split('T')[0], unit_id: "", vendor: "", is_tax_deductible: false });
      refreshData();
    } catch (error) {
      toast.error("Failed to record transaction");
    }
  };

  const handleDeleteLedgerTransaction = async (transactionId) => {
    if (!window.confirm("Delete this transaction?")) return;
    try {
      await api.delete(`/ledger/transactions/${transactionId}`);
      toast.success("Transaction deleted");
      refreshData();
    } catch (error) {
      toast.error("Failed to delete transaction");
    }
  };

  const downloadPnlReport = async () => {
    try {
      const response = await api.get(`/ledger/pnl-report/pdf?property_id=${id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pnl_report_${property?.name?.replace(/\s+/g, '_') || 'property'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("P&L Report downloaded!");
    } catch (error) {
      toast.error("Failed to download report");
    }
  };

  const downloadTaxReport = async () => {
    try {
      const year = new Date().getFullYear();
      const response = await api.get(`/ledger/tax-report/pdf?year=${year}&property_id=${id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tax_report_${year}_${property?.name?.replace(/\s+/g, '_') || 'property'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Tax Report downloaded!");
    } catch (error) {
      toast.error("Failed to download report");
    }
  };

  const handleScanReceipt = async () => {
    if (!receiptUrl.trim()) {
      toast.error("Please enter a receipt image URL");
      return;
    }
    setReceiptScanning(true);
    setReceiptResult(null);
    try {
      const response = await api.post(`/ledger/receipt-ocr?receipt_url=${encodeURIComponent(receiptUrl)}`);
      setReceiptResult(response.data);
      if (response.data.confidence > 0.5) {
        toast.success("Receipt scanned successfully!");
      } else {
        toast.warning("Could not extract all details. Please verify.");
      }
    } catch (error) {
      toast.error("Failed to scan receipt");
    } finally {
      setReceiptScanning(false);
    }
  };

  const applyReceiptToForm = () => {
    if (!receiptResult) return;
    setLedgerForm({
      ...ledgerForm,
      transaction_type: "expense",
      category: receiptResult.category || "",
      amount: receiptResult.amount?.toString() || "",
      description: receiptResult.description || "",
      date: receiptResult.date || new Date().toISOString().split('T')[0],
      vendor: receiptResult.vendor || "",
      is_tax_deductible: true
    });
    setReceiptDialogOpen(false);
    setLedgerDialogOpen(true);
    setReceiptUrl("");
    setReceiptResult(null);
    toast.success("Receipt data applied to form!");
  };

  if (loading) {
    return (
      <Layout title="Property Details">
        <div className="flex items-center justify-center h-64">
          <div className="text-zinc-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  if (!property) return null;

  const totalRentCollected = rentCollections.reduce((sum, r) => sum + r.amount, 0);
  const occupiedUnits = units.filter(u => u.is_occupied).length;
  const pendingMaintenance = maintenance.filter(m => m.status !== "completed").length;
  const mortgageProgress = property.has_mortgage && property.mortgage_amount 
    ? ((property.amount_paid || 0) / property.mortgage_amount) * 100 : 0;

  // Ledger calculations - combine ledger transactions and property expenses
  const allTransactions = [
    ...ledgerTransactions.map(t => ({...t, type: t.transaction_type})),
    ...propertyExpenses.map(exp => ({
      id: exp.id,
      type: 'expense',
      transaction_type: 'expense',
      category: exp.category,
      amount: exp.amount,
      description: exp.name + (exp.description ? ` - ${exp.description}` : ''),
      date: exp.date,
      source: 'expenses'
    }))
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  const totalLedgerIncome = ledgerTransactions.filter(t => t.transaction_type === 'income').reduce((sum, t) => sum + t.amount, 0) + totalRentCollected;
  const totalLedgerExpenses = ledgerTransactions.filter(t => t.transaction_type === 'expense').reduce((sum, t) => sum + t.amount, 0) 
    + propertyExpenses.reduce((sum, e) => sum + e.amount, 0)
    + maintenance.filter(m => m.status === 'completed').reduce((sum, m) => sum + (m.actual_cost || m.estimated_cost), 0);
  const netIncome = totalLedgerIncome - totalLedgerExpenses;

  return (
    <Layout title={property.name}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <Button variant="ghost" onClick={() => navigate("/real-estate")} className="text-zinc-400 hover:text-white" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Properties
          </Button>
          <div className="flex flex-wrap gap-2">
            {/* House Flip Analysis Button */}
            <Dialog open={flipDialogOpen} onOpenChange={setFlipDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-violet-500/50 text-violet-400 hover:bg-violet-500/10 rounded-sm" data-testid="flip-analysis-btn">
                  <Bot className="w-4 h-4 mr-2" /> House Flip Analysis
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A0A0A] border-violet-500/30 rounded-none max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-violet-500" /> AI House Flipping Analysis
                  </DialogTitle>
                  <DialogDescription className="text-zinc-500">
                    Get AI analysis for house flipping potential
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="p-4 bg-white/5 border border-white/10">
                    <p className="text-sm text-zinc-400">Property: <span className="text-white">{property.name}</span></p>
                    <p className="text-sm text-zinc-400">Address: <span className="text-white">{property.address}</span></p>
                    <p className="text-sm text-zinc-400">Purchase Price: <span className="text-amber-500 font-mono">{formatCurrency(property.purchase_price)}</span></p>
                  </div>
                  <Button onClick={runFlipAnalysis} disabled={flipLoading} className="w-full bg-violet-500 text-white hover:bg-violet-600 rounded-sm" data-testid="run-flip-btn">
                    {flipLoading ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" /> Analyzing...</> : "Run Analysis"}
                  </Button>
                  {flipResult && (
                    <div className="p-4 bg-violet-500/10 border border-violet-500/30 rounded-sm">
                      <p className="text-xs font-mono uppercase tracking-widest text-violet-400 mb-2">AI Analysis</p>
                      <div className="text-sm text-zinc-300 whitespace-pre-wrap">{flipResult}</div>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            <Link to={`/real-estate/${id}/ledger`}>
              <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 rounded-sm" data-testid="property-ledger-btn">
                <BookOpen className="w-4 h-4 mr-2" /> Bookkeeping Ledger
              </Button>
            </Link>

            <Button variant="outline" onClick={handleDeleteProperty} className="border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-sm" data-testid="delete-property-btn">
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4">
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Current Value</p>
              <p className="text-2xl font-bold text-amber-500 font-mono">{formatCurrency(property.current_value)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4">
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Units</p>
              <p className="text-2xl font-bold text-white font-mono">{occupiedUnits}/{units.length}</p>
              <p className="text-xs text-zinc-500">occupied</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4">
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Rent Collected</p>
              <p className="text-2xl font-bold text-primary font-mono">{formatCurrency(totalRentCollected)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4">
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Pending Maintenance</p>
              <p className="text-2xl font-bold text-orange-500 font-mono">{pendingMaintenance}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue={property.has_mortgage ? "mortgage" : "units"} className="w-full">
          <TabsList className="bg-[#0A0A0A] border border-white/10 rounded-none flex-wrap">
            {property.has_mortgage && (
              <TabsTrigger value="mortgage" className="rounded-none data-[state=active]:bg-white/10">
                <Landmark className="w-4 h-4 mr-2" /> Mortgage
              </TabsTrigger>
            )}
            <TabsTrigger value="units" className="rounded-none data-[state=active]:bg-white/10">
              <Home className="w-4 h-4 mr-2" /> Units
            </TabsTrigger>
            <TabsTrigger value="rent" className="rounded-none data-[state=active]:bg-white/10">
              <DollarSign className="w-4 h-4 mr-2" /> Rent
            </TabsTrigger>
            <TabsTrigger value="maintenance" className="rounded-none data-[state=active]:bg-white/10">
              <Wrench className="w-4 h-4 mr-2" /> Maintenance
            </TabsTrigger>
            <TabsTrigger value="ledger" className="rounded-none data-[state=active]:bg-white/10">
              <BookOpen className="w-4 h-4 mr-2" /> Ledger
            </TabsTrigger>
          </TabsList>

          {/* Mortgage Tab */}
          {property.has_mortgage && (
            <TabsContent value="mortgage" className="mt-4 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                  <CardContent className="p-4">
                    <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Original Loan</p>
                    <p className="text-xl font-bold text-white font-mono">{formatCurrency(property.mortgage_amount)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                  <CardContent className="p-4">
                    <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Amount Owed</p>
                    <p className="text-xl font-bold text-red-500 font-mono">{formatCurrency(property.amount_owed)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                  <CardContent className="p-4">
                    <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Monthly Payment</p>
                    <p className="text-xl font-bold text-amber-500 font-mono">{formatCurrency(property.monthly_payment)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                  <CardContent className="p-4">
                    <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Interest Rate</p>
                    <p className="text-xl font-bold text-violet-500 font-mono">{formatPercent(property.mortgage_interest_rate)}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Progress */}
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-zinc-400">Payoff Progress</span>
                    <span className="text-sm font-mono text-primary">{mortgageProgress.toFixed(1)}%</span>
                  </div>
                  <div className="h-4 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-primary to-emerald-400" style={{ width: `${Math.min(mortgageProgress, 100)}%` }} />
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-zinc-500">
                    <span>Paid: {formatCurrency(property.amount_paid)}</span>
                    <span>Remaining: {formatCurrency(property.amount_owed)}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <Dialog open={mortgagePaymentDialogOpen} onOpenChange={setMortgagePaymentDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-primary text-black font-bold rounded-sm" data-testid="record-payment-btn">
                      <Plus className="w-4 h-4 mr-2" /> Record Payment
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none">
                    <DialogHeader>
                      <DialogTitle className="text-white">Record Mortgage Payment</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleMortgagePayment} className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Payment Amount</Label>
                        <Input type="number" step="0.01" value={mortgagePaymentForm.amount} onChange={(e) => setMortgagePaymentForm({ ...mortgagePaymentForm, amount: e.target.value })} placeholder={property.monthly_payment?.toString()} required className="bg-black/50 border-white/10 font-mono" data-testid="payment-amount" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300 flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" /> Extra Principal</Label>
                        <Input type="number" step="0.01" value={mortgagePaymentForm.extra_principal} onChange={(e) => setMortgagePaymentForm({ ...mortgagePaymentForm, extra_principal: e.target.value })} placeholder="0" className="bg-black/50 border-white/10 font-mono" data-testid="extra-principal" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Date</Label>
                        <Input type="date" value={mortgagePaymentForm.payment_date} onChange={(e) => setMortgagePaymentForm({ ...mortgagePaymentForm, payment_date: e.target.value })} className="bg-black/50 border-white/10" />
                      </div>
                      <Button type="submit" className="w-full bg-primary text-black font-bold">Record Payment</Button>
                    </form>
                  </DialogContent>
                </Dialog>

                <Dialog open={mortgageAnalysisOpen} onOpenChange={setMortgageAnalysisOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-violet-500/50 text-violet-400 hover:bg-violet-500/10 rounded-sm" data-testid="mortgage-ai-btn">
                      <PiggyBank className="w-4 h-4 mr-2" /> AI Payoff Analysis
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0A0A0A] border-violet-500/30 rounded-none max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-white flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-violet-500" /> AI Mortgage Payoff Analysis
                      </DialogTitle>
                      <DialogDescription className="text-zinc-500">Get strategies to pay off your mortgage faster</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="p-4 bg-white/5 border border-white/10 grid grid-cols-2 gap-4 text-sm">
                        <div><p className="text-zinc-500">Balance</p><p className="text-lg font-mono text-red-500">{formatCurrency(property.amount_owed)}</p></div>
                        <div><p className="text-zinc-500">Rate</p><p className="text-lg font-mono text-violet-500">{formatPercent(property.mortgage_interest_rate)}</p></div>
                        <div><p className="text-zinc-500">Payment</p><p className="text-lg font-mono text-amber-500">{formatCurrency(property.monthly_payment)}</p></div>
                        <div><p className="text-zinc-500">Term</p><p className="text-lg font-mono text-white">{property.mortgage_term_years} years</p></div>
                      </div>
                      <Button onClick={runMortgageAnalysis} disabled={mortgageAnalysisLoading} className="w-full bg-violet-500 text-white hover:bg-violet-600 rounded-sm">
                        {mortgageAnalysisLoading ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" /> Analyzing...</> : "Analyze Payoff Options"}
                      </Button>
                      {mortgageAnalysisResult && (
                        <div className="p-4 bg-violet-500/10 border border-violet-500/30 rounded-sm">
                          <p className="text-xs font-mono uppercase tracking-widest text-violet-400 mb-2">AI Analysis</p>
                          <div className="text-sm text-zinc-300 whitespace-pre-wrap">{mortgageAnalysisResult.analysis}</div>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Payment History */}
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
                <CardHeader><CardTitle className="text-lg text-white">Payment History</CardTitle></CardHeader>
                <CardContent>
                  {mortgagePayments.length === 0 ? (
                    <div className="text-center py-8 text-zinc-500">No payments recorded yet.</div>
                  ) : (
                    <div className="space-y-2">
                      {mortgagePayments.map((p) => (
                        <div key={p.id} className="flex justify-between p-4 border border-white/5 hover:border-white/10">
                          <div>
                            <p className="text-white">{p.payment_date}</p>
                            <p className="text-xs text-zinc-500">Principal: {formatCurrency(p.principal_portion)} • Interest: {formatCurrency(p.interest_portion)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-primary font-mono">{formatCurrency(p.amount)}</p>
                            <p className="text-xs text-zinc-500">Balance: {formatCurrency(p.balance_after)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Units Tab */}
          <TabsContent value="units" className="mt-4">
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg text-white">Units</CardTitle>
                <Dialog open={unitDialogOpen} onOpenChange={handleUnitDialogClose}>
                  <DialogTrigger asChild>
                    <Button className="bg-primary text-black font-bold rounded-sm" data-testid="add-unit-btn"><Plus className="w-4 h-4 mr-2" /> Add Unit</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none">
                    <DialogHeader><DialogTitle className="text-white">{editingUnit ? "Edit Unit" : "Add Unit"}</DialogTitle></DialogHeader>
                    <form onSubmit={handleAddUnit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Unit Number</Label>
                          <Input value={unitForm.unit_number} onChange={(e) => setUnitForm({ ...unitForm, unit_number: e.target.value })} required className="bg-black/50 border-white/10" data-testid="unit-number" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Rent</Label>
                          <Input type="number" value={unitForm.rent_amount} onChange={(e) => setUnitForm({ ...unitForm, rent_amount: e.target.value })} required className="bg-black/50 border-white/10 font-mono" data-testid="unit-rent" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Deposit</Label>
                          <Input type="number" value={unitForm.deposit} onChange={(e) => setUnitForm({ ...unitForm, deposit: e.target.value })} className="bg-black/50 border-white/10 font-mono" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Tenant Name</Label>
                          <Input value={unitForm.tenant_name} onChange={(e) => setUnitForm({ ...unitForm, tenant_name: e.target.value })} className="bg-black/50 border-white/10" placeholder="Lease holder name" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Tenant Email</Label>
                          <Input type="email" value={unitForm.tenant_email} onChange={(e) => setUnitForm({ ...unitForm, tenant_email: e.target.value })} className="bg-black/50 border-white/10" placeholder="tenant@email.com" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Tenant Phone</Label>
                          <Input type="tel" value={unitForm.tenant_phone} onChange={(e) => setUnitForm({ ...unitForm, tenant_phone: e.target.value })} className="bg-black/50 border-white/10" placeholder="(555) 123-4567" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Lease Start</Label>
                          <Input type="date" value={unitForm.lease_start || ''} onChange={(e) => setUnitForm({ ...unitForm, lease_start: e.target.value })} className="bg-black/50 border-white/10" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Lease End</Label>
                          <Input type="date" value={unitForm.lease_end || ''} onChange={(e) => setUnitForm({ ...unitForm, lease_end: e.target.value })} className="bg-black/50 border-white/10" />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <input type="checkbox" id="occupied" checked={unitForm.is_occupied} onChange={(e) => setUnitForm({ ...unitForm, is_occupied: e.target.checked })} />
                        <Label htmlFor="occupied" className="text-zinc-300">Currently Occupied</Label>
                      </div>
                      <Button type="submit" className="w-full bg-primary text-black font-bold" disabled={submitting}>
                        {submitting ? "Saving..." : (editingUnit ? "Update Unit" : "Add Unit")}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {units.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">No units added yet.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {units.map((unit) => (
                      <div key={unit.id} className="p-4 border border-white/10 hover:border-white/20 transition-colors">
                        <div className="flex justify-between mb-3">
                          <span className="text-lg font-bold text-white">Unit {unit.unit_number}</span>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditUnit(unit)}
                              className="h-6 w-6 text-zinc-500 hover:text-blue-500 hover:bg-blue-500/10"
                              data-testid={`edit-unit-${unit.id}`}
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <span className={`text-xs px-2 py-1 rounded ${unit.is_occupied ? 'bg-primary/20 text-primary' : 'bg-zinc-500/20 text-zinc-400'}`}>
                              {unit.is_occupied ? 'Occupied' : 'Vacant'}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-zinc-500">Tenant</span>
                            <span className="text-white font-medium">{unit.tenant_name || '-'}</span>
                          </div>
                          {unit.tenant_email && (
                            <div className="flex justify-between items-center">
                              <span className="text-zinc-500 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</span>
                              <a href={`mailto:${unit.tenant_email}`} className="text-primary hover:underline text-xs">{unit.tenant_email}</a>
                            </div>
                          )}
                          {unit.tenant_phone && (
                            <div className="flex justify-between items-center">
                              <span className="text-zinc-500 flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</span>
                              <a href={`tel:${unit.tenant_phone}`} className="text-primary hover:underline text-xs">{unit.tenant_phone}</a>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-zinc-500">Monthly Rent</span>
                            <span className="font-mono text-primary">{formatCurrency(unit.rent_amount)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-zinc-500">Deposit</span>
                            <span className="font-mono text-white">{formatCurrency(unit.deposit)}</span>
                          </div>
                          {unit.lease_start && (
                            <div className="flex justify-between">
                              <span className="text-zinc-500">Lease</span>
                              <span className="text-white text-xs">{unit.lease_start} - {unit.lease_end || 'Present'}</span>
                            </div>
                          )}
                        </div>
                        <Link to={`/unit/${unit.id}/rent-ledger`}>
                          <Button 
                            variant="outline" 
                            className="w-full mt-3 border-primary/50 text-primary hover:bg-primary/10 rounded-sm"
                            data-testid={`unit-ledger-btn-${unit.id}`}
                          >
                            <BookOpen className="w-4 h-4 mr-2" />
                            12-Month Rent Ledger
                          </Button>
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rent Tab */}
          <TabsContent value="rent" className="mt-4">
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg text-white">Rent Collection</CardTitle>
                <Dialog open={rentDialogOpen} onOpenChange={setRentDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-primary text-black font-bold rounded-sm" data-testid="collect-rent-btn"><Plus className="w-4 h-4 mr-2" /> Collect Rent</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none">
                    <DialogHeader><DialogTitle className="text-white">Collect Rent</DialogTitle></DialogHeader>
                    <form onSubmit={handleCollectRent} className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Unit</Label>
                        <Select value={rentForm.unit_id} onValueChange={(v) => setRentForm({ ...rentForm, unit_id: v })} required>
                          <SelectTrigger className="bg-black/50 border-white/10"><SelectValue placeholder="Select unit" /></SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-white/10">
                            {units.map((u) => <SelectItem key={u.id} value={u.id}>Unit {u.unit_number}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Amount</Label>
                        <Input type="number" value={rentForm.amount} onChange={(e) => setRentForm({ ...rentForm, amount: e.target.value })} required className="bg-black/50 border-white/10 font-mono" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Method</Label>
                        <Select value={rentForm.payment_method} onValueChange={(v) => setRentForm({ ...rentForm, payment_method: v })} required>
                          <SelectTrigger className="bg-black/50 border-white/10"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-white/10">
                            <SelectItem value="zelle">Zelle</SelectItem>
                            <SelectItem value="cash">Cash</SelectItem>
                            <SelectItem value="check">Check</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button type="submit" className="w-full bg-primary text-black font-bold">Record Payment</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {rentCollections.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">No rent collected yet.</div>
                ) : (
                  <div className="space-y-2">
                    {rentCollections.map((r) => {
                      const unit = units.find(u => u.id === r.unit_id);
                      return (
                        <div key={r.id} className="flex justify-between p-4 border border-white/5 hover:border-white/10">
                          <div>
                            <p className="text-white">Unit {unit?.unit_number || 'N/A'}</p>
                            <p className="text-sm text-zinc-500">{r.payment_method.toUpperCase()} • {r.date}</p>
                          </div>
                          <p className="text-lg font-bold text-primary font-mono">+{formatCurrency(r.amount)}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Maintenance Tab */}
          <TabsContent value="maintenance" className="mt-4">
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg text-white">Maintenance</CardTitle>
                <Dialog open={maintenanceDialogOpen} onOpenChange={setMaintenanceDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-primary text-black font-bold rounded-sm" data-testid="add-maintenance-btn"><Plus className="w-4 h-4 mr-2" /> Add Request</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none">
                    <DialogHeader><DialogTitle className="text-white">Add Maintenance Request</DialogTitle></DialogHeader>
                    <form onSubmit={handleAddMaintenance} className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Unit</Label>
                        <Select value={maintenanceForm.unit_id || "common"} onValueChange={(v) => setMaintenanceForm({ ...maintenanceForm, unit_id: v === "common" ? "" : v })}>
                          <SelectTrigger className="bg-black/50 border-white/10"><SelectValue placeholder="Common Area" /></SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-white/10">
                            <SelectItem value="common">Common Area</SelectItem>
                            {units.map((u) => <SelectItem key={u.id} value={u.id}>Unit {u.unit_number}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Title</Label>
                        <Input value={maintenanceForm.title} onChange={(e) => setMaintenanceForm({ ...maintenanceForm, title: e.target.value })} required className="bg-black/50 border-white/10" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Description</Label>
                        <Input value={maintenanceForm.description} onChange={(e) => setMaintenanceForm({ ...maintenanceForm, description: e.target.value })} required className="bg-black/50 border-white/10" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Est. Cost</Label>
                        <Input type="number" value={maintenanceForm.estimated_cost} onChange={(e) => setMaintenanceForm({ ...maintenanceForm, estimated_cost: e.target.value })} className="bg-black/50 border-white/10 font-mono" />
                      </div>
                      <Button type="submit" className="w-full bg-primary text-black font-bold">Add Request</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {maintenance.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">No maintenance requests.</div>
                ) : (
                  <div className="space-y-2">
                    {maintenance.map((m) => {
                      const unit = units.find(u => u.id === m.unit_id);
                      const Icon = m.status === "completed" ? CheckCircle : m.status === "in_progress" ? Clock : AlertCircle;
                      const color = m.status === "completed" ? "text-primary" : m.status === "in_progress" ? "text-blue-500" : "text-orange-500";
                      return (
                        <div key={m.id} className="flex justify-between items-center p-4 border border-white/5 hover:border-white/10">
                          <div className="flex items-center gap-3">
                            <Icon className={`w-5 h-5 ${color}`} />
                            <div>
                              <p className="text-white">{m.title}</p>
                              <p className="text-sm text-zinc-500">{unit ? `Unit ${unit.unit_number}` : 'Common'} • Est: {formatCurrency(m.estimated_cost)}</p>
                            </div>
                          </div>
                          {m.status !== "completed" ? (
                            <Button size="sm" variant="outline" onClick={() => handleCompleteMaintenance(m.id, m.estimated_cost)} className="text-primary border-primary/50">Complete</Button>
                          ) : (
                            <span className="text-sm font-mono text-red-500">-{formatCurrency(m.actual_cost || m.estimated_cost)}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ledger Tab */}
          <TabsContent value="ledger" className="mt-4">
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <BookOpen className="w-5 h-5 text-teal-400" />
                  Property Ledger
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    className="border-green-500/50 text-green-400 hover:bg-green-500/10 rounded-sm"
                    onClick={downloadPnlReport}
                    data-testid="download-pnl-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" /> P&L Report
                  </Button>
                  <Button 
                    variant="outline" 
                    className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 rounded-sm"
                    onClick={downloadTaxReport}
                    data-testid="download-tax-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" /> Tax Report
                  </Button>
                  
                  {/* Receipt Scanning Dialog */}
                  <Dialog open={receiptDialogOpen} onOpenChange={setReceiptDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 rounded-sm"
                        data-testid="scan-receipt-btn"
                      >
                        <Receipt className="w-4 h-4 mr-2" /> Scan Receipt
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#0A0A0A] border-amber-500/30 rounded-none max-w-lg">
                      <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                          <Receipt className="w-5 h-5 text-amber-500" /> AI Receipt Scanner
                        </DialogTitle>
                        <DialogDescription className="text-zinc-500">
                          Enter a URL to a receipt image. AI will extract vendor, amount, date, and category.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Receipt Image URL</Label>
                          <Input 
                            value={receiptUrl}
                            onChange={(e) => setReceiptUrl(e.target.value)}
                            placeholder="https://example.com/receipt.jpg"
                            className="bg-black/50 border-white/10 rounded-sm"
                            data-testid="receipt-url-input"
                          />
                          <p className="text-xs text-zinc-500">Supports JPG, PNG, or PDF URLs</p>
                        </div>
                        <Button 
                          onClick={handleScanReceipt}
                          disabled={receiptScanning || !receiptUrl.trim()}
                          className="w-full bg-amber-500 text-black hover:bg-amber-600 rounded-sm"
                          data-testid="scan-receipt-submit-btn"
                        >
                          {receiptScanning ? (
                            <><span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" /> Scanning...</>
                          ) : (
                            <>Scan Receipt</>
                          )}
                        </Button>
                        
                        {receiptResult && (
                          <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm space-y-3">
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-mono uppercase tracking-widest text-amber-400">Extracted Data</p>
                              <span className={`text-xs px-2 py-1 rounded ${receiptResult.confidence > 0.7 ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
                                {Math.round(receiptResult.confidence * 100)}% confidence
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <p className="text-zinc-500 text-xs">Vendor</p>
                                <p className="text-white">{receiptResult.vendor || '-'}</p>
                              </div>
                              <div>
                                <p className="text-zinc-500 text-xs">Amount</p>
                                <p className="text-amber-400 font-mono">{receiptResult.amount ? formatCurrency(receiptResult.amount) : '-'}</p>
                              </div>
                              <div>
                                <p className="text-zinc-500 text-xs">Date</p>
                                <p className="text-white">{receiptResult.date || '-'}</p>
                              </div>
                              <div>
                                <p className="text-zinc-500 text-xs">Category</p>
                                <p className="text-white capitalize">{receiptResult.category?.replace(/_/g, ' ') || '-'}</p>
                              </div>
                            </div>
                            {receiptResult.description && (
                              <div>
                                <p className="text-zinc-500 text-xs">Description</p>
                                <p className="text-white text-sm">{receiptResult.description}</p>
                              </div>
                            )}
                            <Button 
                              onClick={applyReceiptToForm}
                              className="w-full bg-teal-500 text-black hover:bg-teal-600 rounded-sm mt-2"
                              data-testid="apply-receipt-btn"
                            >
                              Apply to Transaction Form
                            </Button>
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={ledgerDialogOpen} onOpenChange={setLedgerDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-teal-500 text-black hover:bg-teal-600 rounded-sm" data-testid="add-ledger-btn">
                        <Plus className="w-4 h-4 mr-2" /> Add Transaction
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none">
                      <DialogHeader>
                        <DialogTitle className="text-white" style={{ fontFamily: 'Manrope' }}>Add Ledger Transaction</DialogTitle>
                        <DialogDescription className="text-zinc-500">Record income or expense for this property</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleAddLedgerTransaction} className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Type</Label>
                          <Select value={ledgerForm.transaction_type} onValueChange={(v) => setLedgerForm({...ledgerForm, transaction_type: v, category: ""})}>
                            <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0A0A0A] border-white/10">
                              <SelectItem value="income">Income</SelectItem>
                              <SelectItem value="expense">Expense</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Category</Label>
                        <Select value={ledgerForm.category} onValueChange={(v) => setLedgerForm({...ledgerForm, category: v})}>
                          <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-white/10">
                            {ledgerForm.transaction_type === 'income' ? (
                              <>
                                <SelectItem value="rent">Rent</SelectItem>
                                <SelectItem value="late_fee">Late Fee</SelectItem>
                                <SelectItem value="parking">Parking</SelectItem>
                                <SelectItem value="laundry">Laundry</SelectItem>
                                <SelectItem value="other_income">Other Income</SelectItem>
                              </>
                            ) : (
                              <>
                                <SelectItem value="repairs">Repairs</SelectItem>
                                <SelectItem value="maintenance">Maintenance</SelectItem>
                                <SelectItem value="utilities">Utilities</SelectItem>
                                <SelectItem value="insurance">Insurance</SelectItem>
                                <SelectItem value="property_tax">Property Tax</SelectItem>
                                <SelectItem value="management_fee">Management Fee</SelectItem>
                                <SelectItem value="supplies">Supplies</SelectItem>
                                <SelectItem value="landscaping">Landscaping</SelectItem>
                                <SelectItem value="cleaning">Cleaning</SelectItem>
                                <SelectItem value="advertising">Advertising</SelectItem>
                                <SelectItem value="legal">Legal/Professional</SelectItem>
                                <SelectItem value="other_expense">Other Expense</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      {units.length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Unit (Optional)</Label>
                          <Select value={ledgerForm.unit_id || "property-wide"} onValueChange={(v) => setLedgerForm({...ledgerForm, unit_id: v === "property-wide" ? "" : v})}>
                            <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                              <SelectValue placeholder="Select unit (optional)" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0A0A0A] border-white/10">
                              <SelectItem value="property-wide">Property-wide</SelectItem>
                              {units.map(u => (
                                <SelectItem key={u.id} value={u.id}>Unit {u.unit_number}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Amount</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={ledgerForm.amount}
                          onChange={(e) => setLedgerForm({...ledgerForm, amount: e.target.value})}
                          className="bg-black/50 border-white/10 rounded-sm font-mono"
                          placeholder="0.00"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Date</Label>
                        <Input
                          type="date"
                          value={ledgerForm.date}
                          onChange={(e) => setLedgerForm({...ledgerForm, date: e.target.value})}
                          className="bg-black/50 border-white/10 rounded-sm"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Description</Label>
                        <Input
                          value={ledgerForm.description}
                          onChange={(e) => setLedgerForm({...ledgerForm, description: e.target.value})}
                          className="bg-black/50 border-white/10 rounded-sm"
                          placeholder="e.g., Plumbing repair"
                        />
                      </div>
                      {ledgerForm.transaction_type === 'expense' && (
                        <>
                          <div className="space-y-2">
                            <Label className="text-zinc-300">Vendor (Optional)</Label>
                            <Input
                              value={ledgerForm.vendor}
                              onChange={(e) => setLedgerForm({...ledgerForm, vendor: e.target.value})}
                              className="bg-black/50 border-white/10 rounded-sm"
                              placeholder="e.g., ABC Plumbing"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="tax-deductible"
                              checked={ledgerForm.is_tax_deductible}
                              onChange={(e) => setLedgerForm({...ledgerForm, is_tax_deductible: e.target.checked})}
                              className="w-4 h-4 rounded bg-black/50 border-white/20 text-teal-500"
                            />
                            <label htmlFor="tax-deductible" className="text-zinc-300 text-sm">
                              Tax Deductible (Schedule E)
                            </label>
                          </div>
                        </>
                      )}
                      <Button type="submit" className="w-full bg-teal-500 text-black hover:bg-teal-600 rounded-sm">
                        Add Transaction
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              </CardHeader>
              <CardContent>
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-green-500/10 border border-green-500/30 rounded p-4">
                    <p className="text-xs text-green-400 uppercase tracking-widest">Total Income</p>
                    <p className="text-2xl font-bold text-green-400 font-mono">{formatCurrency(totalLedgerIncome)}</p>
                  </div>
                  <div className="bg-red-500/10 border border-red-500/30 rounded p-4">
                    <p className="text-xs text-red-400 uppercase tracking-widest">Total Expenses</p>
                    <p className="text-2xl font-bold text-red-400 font-mono">{formatCurrency(totalLedgerExpenses)}</p>
                  </div>
                  <div className={`${netIncome >= 0 ? 'bg-teal-500/10 border-teal-500/30' : 'bg-orange-500/10 border-orange-500/30'} border rounded p-4`}>
                    <p className={`text-xs ${netIncome >= 0 ? 'text-teal-400' : 'text-orange-400'} uppercase tracking-widest`}>Net Income</p>
                    <p className={`text-2xl font-bold ${netIncome >= 0 ? 'text-teal-400' : 'text-orange-400'} font-mono`}>{formatCurrency(netIncome)}</p>
                  </div>
                </div>

                {/* Transaction List */}
                {allTransactions.length === 0 && rentCollections.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    No transactions recorded yet. Add income or expenses to track your property finances.
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">All Transactions</p>
                    
                    {/* Show rent collections as income */}
                    {rentCollections.map((rent) => (
                      <div
                        key={`rent-${rent.id}`}
                        className="flex items-center justify-between p-3 border border-white/5 hover:border-green-500/30 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded flex items-center justify-center bg-green-500/20">
                            <DollarSign className="w-4 h-4 text-green-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">Rent Collection</p>
                            <p className="text-xs text-zinc-500">{rent.date} • {rent.payment_method}</p>
                          </div>
                        </div>
                        <p className="text-green-400 font-mono font-bold">+{formatCurrency(rent.amount)}</p>
                      </div>
                    ))}

                    {/* Show ledger transactions and expenses */}
                    {allTransactions.map((transaction) => (
                      <div
                        key={transaction.id}
                        className={`flex items-center justify-between p-3 border border-white/5 hover:border-${transaction.type === 'income' ? 'green' : 'red'}-500/30 transition-colors`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded flex items-center justify-center ${transaction.type === 'income' ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                            {transaction.type === 'income' ? (
                              <DollarSign className="w-4 h-4 text-green-400" />
                            ) : (
                              <Trash2 className="w-4 h-4 text-red-400" />
                            )}
                          </div>
                          <div>
                            <p className="text-white font-medium capitalize">{transaction.category?.replace(/_/g, ' ') || 'Other'}</p>
                            <p className="text-xs text-zinc-500">
                              {transaction.date}
                              {transaction.description && ` • ${transaction.description}`}
                              {transaction.source === 'expenses' && <span className="text-orange-400 ml-1">(from Expenses)</span>}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <p className={`font-mono font-bold ${transaction.type === 'income' ? 'text-green-400' : 'text-red-400'}`}>
                            {transaction.type === 'income' ? '+' : '-'}{formatCurrency(transaction.amount)}
                          </p>
                          {transaction.source !== 'expenses' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteLedgerTransaction(transaction.id)}
                              className="text-zinc-500 hover:text-red-500 hover:bg-red-500/10 h-8 w-8"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
